import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Lightbulb } from 'lucide-react';

const isMobile = () => window.innerWidth < 768;
const sleep = ms => new Promise(r => setTimeout(r, ms));

function waitForEl(sel, timeout = 2000) {
    return new Promise(resolve => {
        const el = document.querySelector(sel);
        if (el) return resolve(el);
        const obs = new MutationObserver(() => {
            const found = document.querySelector(sel);
            if (found) { obs.disconnect(); resolve(found); }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => { obs.disconnect(); resolve(null); }, timeout);
    });
}

const storageKey = (pageKey) => `sv_ctour_${pageKey}`;

export function ContextualTour({ pageKey, steps, delay = 1500 }) {
    const [active, setActive] = useState(false);
    const [stepIdx, setStepIdx] = useState(0);
    const [pos, setPos] = useState({ top: 100, left: 16 });
    const [ready, setReady] = useState(false);
    const prevElRef = useRef(null);

    const step = steps[stepIdx];

    useEffect(() => {
        if (localStorage.getItem(storageKey(pageKey))) return;
        let timer = null;
        let bioListener = null;
        let tourListener = null;
        let mounted = true;

        const MINI_TOUR_DELAY = 10000; // 10 seconds after biometric resolved

        const showMiniTour = (ms) => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => { if (mounted) setActive(true); }, ms);
        };

        const waitForBiometric = () => {
            // Listen for biometric resolution. Only start mini-tour if CONFIRMED.
            bioListener = (e) => {
                window.removeEventListener('sv_biometric_resolved', bioListener);
                bioListener = null;
                const confirmed = e?.detail?.confirmed === true;
                if (confirmed) {
                    showMiniTour(MINI_TOUR_DELAY);
                }
                // If denied/closed/later → do NOT show mini-tour this session
            };
            window.addEventListener('sv_biometric_resolved', bioListener);
        };

        // ---- Determine state ----
        const tourSiteDone = localStorage.getItem('sv_tour_site_done');
        const tourSystemDone = localStorage.getItem('sv_tour_system_done');
        const tourCompleted = tourSiteDone || tourSystemDone; // Main tour ever completed?
        const tourJustNow = sessionStorage.getItem('sv_tour_just_completed'); // Completed THIS session?
        const biometricPref = localStorage.getItem('sv_biometric_pref');
        const biometricDone = biometricPref === 'enabled' || biometricPref === 'never';

        if (!tourCompleted) {
            // CASE 1: Main tour NOT yet done → it's either running or about to start.
            // Do NOT show contextual tour. Wait for tour to complete first.
            tourListener = () => {
                window.removeEventListener('sv_tour_completed', tourListener);
                tourListener = null;
                // Tour just finished. Now wait for biometric.
                if (biometricDone) {
                    // Biometric was already handled in a previous interaction
                    showMiniTour(MINI_TOUR_DELAY);
                } else {
                    waitForBiometric();
                }
            };
            window.addEventListener('sv_tour_completed', tourListener);

        } else if (tourJustNow && !biometricDone) {
            // CASE 2: Tour completed THIS session, biometric not yet resolved.
            // Wait for biometric resolved → 10s → show.
            waitForBiometric();

        } else if (tourJustNow && biometricDone) {
            // CASE 3: Tour completed THIS session AND biometric already handled.
            // Show after 10s.
            showMiniTour(MINI_TOUR_DELAY);

        } else {
            // CASE 4: Tour was completed in a PREVIOUS session. Normal page visit.
            // Standard delay.
            showMiniTour(delay);
        }

        return () => {
            mounted = false;
            if (timer) clearTimeout(timer);
            if (bioListener) window.removeEventListener('sv_biometric_resolved', bioListener);
            if (tourListener) window.removeEventListener('sv_tour_completed', tourListener);
        };
    }, [pageKey, delay]);

    const cleanup = useCallback(() => {
        if (prevElRef.current) {
            try {
                prevElRef.current.style.zIndex = '';
                prevElRef.current.style.boxShadow = '';
                prevElRef.current.style.borderRadius = '';
            } catch {}
            prevElRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!active || !step) return;
        let cancelled = false;
        setReady(false);

        const run = async () => {
            cleanup();
            const mobile = isMobile();
            await sleep(150);
            if (cancelled) return;

            const targetSel = step.target;
            if (!targetSel) {
                setPos({
                    top: mobile ? Math.max(window.innerHeight / 2 - 80, 80) : window.innerHeight / 2 - 80,
                    left: mobile ? 16 : Math.max(window.innerWidth / 2 - 150, 16),
                });
                setReady(true);
                return;
            }

            const el = await waitForEl(targetSel, 2000);
            if (cancelled) return;
            if (!el) { setPos({ top: 100, left: 16 }); setReady(true); return; }

            const rect = el.getBoundingClientRect();
            if (rect.top < 60 || rect.bottom > window.innerHeight - 60) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                await sleep(300);
                if (cancelled) return;
            }

            el.style.zIndex = '100001';
            el.style.boxShadow = '0 0 0 3px rgba(var(--sv-accent-rgb), 0.4), 0 0 16px rgba(var(--sv-accent-rgb), 0.12)';
            el.style.borderRadius = '8px';
            prevElRef.current = el;

            const r = el.getBoundingClientRect();
            const tH = 160, pad = 12;
            let top, left;
            if (mobile) {
                left = 12;
                top = r.bottom + pad > window.innerHeight - tH - 10
                    ? Math.max(10, r.top - tH - pad)
                    : r.bottom + pad;
                top = Math.max(10, Math.min(top, window.innerHeight - tH - 10));
            } else {
                top = r.bottom + pad > window.innerHeight - tH - 10
                    ? Math.max(10, r.top - tH - pad)
                    : r.bottom + pad;
                left = r.left;
                if (left + 300 > window.innerWidth - 20) left = window.innerWidth - 320;
                if (left < 10) left = 10;
                top = Math.max(10, Math.min(top, window.innerHeight - tH - 10));
            }
            setPos({ top, left });
            setReady(true);
        };

        run();
        return () => { cancelled = true; };
    }, [active, stepIdx, step, cleanup]);

    useEffect(() => () => cleanup(), [cleanup]);

    if (!active || !step) return null;

    const mobile = isMobile();
    const isLast = stepIdx === steps.length - 1;
    const total = steps.length;

    const doClose = () => {
        cleanup();
        setActive(false);
        localStorage.setItem(storageKey(pageKey), '1');
        window.dispatchEvent(new CustomEvent('sv_minitour_completed', { detail: { pageKey } }));
    };
    const doNext = () => {
        if (isLast) { doClose(); return; }
        setReady(false);
        setStepIdx(s => s + 1);
    };
    const doPrev = () => {
        setReady(false);
        setStepIdx(s => Math.max(0, s - 1));
    };

    return (
        <>
            <div
                style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,0.4)', transition: 'opacity 0.2s' }}
                onClick={doClose}
                data-testid={`ctour-overlay-${pageKey}`}
            />
            <div
                style={{
                    position: 'fixed', zIndex: 100002,
                    top: pos.top, left: pos.left,
                    background: '#1C1C1E', border: '1px solid rgba(var(--sv-accent-rgb), 0.25)',
                    borderRadius: 14, padding: '16px 18px',
                    maxWidth: mobile ? 'calc(100vw - 24px)' : 300,
                    width: mobile ? 'calc(100vw - 24px)' : 280,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
                    transition: 'top 0.2s ease, left 0.2s ease, opacity 0.2s ease',
                    opacity: ready ? 1 : 0,
                    pointerEvents: ready ? 'auto' : 'none',
                }}
                onClick={e => e.stopPropagation()}
                data-testid={`ctour-tooltip-${pageKey}`}
            >
                <div className="flex items-center gap-2 mb-2">
                    <Lightbulb size={14} style={{ color: 'var(--sv-accent)' }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--sv-accent)', letterSpacing: '0.03em' }}>DICA</span>
                    <button onClick={doClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 2 }}>
                        <X size={12} />
                    </button>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#F5F5F5', marginBottom: 4 }}>{step.title}</div>
                <div style={{ fontSize: 12, color: '#BBBBBB', lineHeight: 1.6, marginBottom: 14 }}>{step.content}</div>
                <div className="flex items-center justify-between">
                    <span style={{ fontSize: 10, color: '#555' }}>{stepIdx + 1}/{total}</span>
                    <div className="flex gap-1.5">
                        {stepIdx > 0 && (
                            <button onClick={doPrev} style={{ background: 'none', color: '#888', border: 'none', fontSize: 12, cursor: 'pointer', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 3 }}>
                                <ChevronLeft size={12} /> Voltar
                            </button>
                        )}
                        <button onClick={doNext} style={{
                            background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)', border: 'none', borderRadius: 10,
                            padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 3, minHeight: 36,
                        }} data-testid={`ctour-next-${pageKey}`}>
                            {isLast ? 'Entendi' : 'Proximo'} {!isLast && <ChevronRight size={12} />}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

// Expose reset for testing
export function resetContextualTour(pageKey) {
    localStorage.removeItem(storageKey(pageKey));
}
